/**
 * Firefly Particles System — Performance-Optimized
 * Lightweight, reusable particle background for Shopify sections.
 * Usage: Add <canvas class="firefly-canvas"> with data attributes:
 *   data-particle-color, data-particle-count, data-particle-speed, data-particle-size
 *
 * Optimizations vs. the previous version:
 * 1. Sprite caching — the glow is pre-rendered once to an offscreen canvas
 *    and blitted with drawImage() instead of building a new radial
 *    gradient for every particle on every single frame.
 * 2. IntersectionObserver — the animation loop only runs while the canvas
 *    is actually visible (100px margin), and is fully stopped
 *    (cancelAnimationFrame) the moment it scrolls off screen.
 * 3. Idle / post-load init — canvases are set up after window "load" and
 *    inside requestIdleCallback, so they never compete with LCP/TBT
 *    during the critical loading window.
 * 4. Mobile particle cap — narrow screens get far fewer particles.
 * 5. Tab-visibility pause — animation stops while the browser tab is
 *    backgrounded (covers browsers that don't already throttle rAF there).
 * 6. Respects prefers-reduced-motion — no animation at all for users who
 *    have that OS/browser setting on.
 *
 * Visual behavior (motion, pulse, colors, wander) is kept identical to the
 * previous version — only the expensive parts were replaced.
 */

class FireflyParticles {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: true });
    this.particles = [];
    this.animationId = null;
    this.isRunning = false;
    this.spriteCanvas = null;
    this.inViewport = false;
    this.tabVisible = !document.hidden;

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const isMobile = window.innerWidth < 768;
    const requestedCount = parseInt(canvas.dataset.particleCount, 10) || 50;

    this.config = {
      color: canvas.dataset.particleColor || '#ffd700',
      count: isMobile ? Math.min(requestedCount, 18) : requestedCount,
      speed: parseFloat(canvas.dataset.particleSpeed) || 1,
      size: parseFloat(canvas.dataset.particleSize) || 1
    };

    this.rgb = this.hexToRgb(this.config.color);

    // Respect the user's OS-level "reduce motion" preference: leave the
    // canvas empty and skip all the animation setup below entirely.
    if (this.prefersReducedMotion) return;

    this.generateSprite();
    this.init();
  }

  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 255, g: 215, b: 0 };
  }

  // Pre-render the glow ONCE onto a small offscreen canvas. Every particle,
  // every frame, then just blits this cached bitmap (cheap GPU copy)
  // instead of rebuilding a gradient (expensive CPU work) each time.
  generateSprite() {
    const size = 32;
    this.spriteCanvas = document.createElement('canvas');
    this.spriteCanvas.width = size * 2;
    this.spriteCanvas.height = size * 2;
    const sCtx = this.spriteCanvas.getContext('2d');

    const grad = sCtx.createRadialGradient(size, size, 0, size, size, size);
    grad.addColorStop(0, `rgba(${this.rgb.r}, ${this.rgb.g}, ${this.rgb.b}, 1)`);
    grad.addColorStop(0.25, `rgba(${this.rgb.r}, ${this.rgb.g}, ${this.rgb.b}, 0.6)`);
    grad.addColorStop(0.6, `rgba(${this.rgb.r}, ${this.rgb.g}, ${this.rgb.b}, 0.2)`);
    grad.addColorStop(1, `rgba(${this.rgb.r}, ${this.rgb.g}, ${this.rgb.b}, 0)`);

    sCtx.fillStyle = grad;
    sCtx.beginPath();
    sCtx.arc(size, size, size, 0, Math.PI * 2);
    sCtx.fill();
  }

  resizeCanvas() {
    if (!this.canvas.offsetWidth || !this.canvas.offsetHeight) return;
    this.canvas.width = this.canvas.offsetWidth;
    this.canvas.height = this.canvas.offsetHeight;
  }

  init() {
    this.resizeCanvas();
    this.createParticles();

    // Only animate while the canvas is actually on screen.
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.inViewport = entry.isIntersecting;
          this.updateRunState();
        });
      }, { rootMargin: '100px' });
      this.observer.observe(this.canvas);
    } else {
      this.inViewport = true;
      this.updateRunState();
    }

    // Pause when the browser tab itself is backgrounded.
    this.visibilityHandler = () => {
      this.tabVisible = !document.hidden;
      this.updateRunState();
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    let resizeTimeout;
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.resizeCanvas(), 300);
    };
    window.addEventListener('resize', this.resizeHandler, { passive: true });
  }

  createParticles() {
    this.particles = [];
    for (let i = 0; i < this.config.count; i++) {
      this.particles.push(new Particle(this.canvas, this.config));
    }
  }

  updateRunState() {
    if (this.inViewport && this.tabVisible) this.start();
    else this.stop();
  }

  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.animate();
  }

  stop() {
    if (!this.isRunning) return;
    this.isRunning = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  animate() {
    if (!this.isRunning) return;

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      p.update();
      p.draw(this.ctx, this.spriteCanvas);
    }

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  destroy() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
    if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler);
  }
}

class Particle {
  constructor(canvas, config) {
    this.canvas = canvas;
    this.config = config;
    this.reset();
  }

  reset() {
    this.x = Math.random() * (this.canvas.width || 300);
    this.y = Math.random() * (this.canvas.height || 400);
    this.vx = (Math.random() - 0.5) * 0.5 * this.config.speed;
    this.vy = (Math.random() - 0.5) * 0.5 * this.config.speed;
    this.baseSize = (Math.random() * 2 + 1) * this.config.size;
    this.phase = Math.random() * Math.PI * 2;
  }

  update() {
    this.x += this.vx;
    this.y += this.vy;
    this.phase += 0.02 * this.config.speed;

    // Same organic "wander" drift as before, just as cheap as it was.
    this.vx += (Math.random() - 0.5) * 0.05;
    this.vy += (Math.random() - 0.5) * 0.05;
    this.vx *= 0.98;
    this.vy *= 0.98;

    const w = this.canvas.width;
    const h = this.canvas.height;
    this.x = ((this.x % w) + w) % w;
    if (this.y < -10) this.y = h + 10;
    if (this.y > h + 10) this.y = -10;
  }

  draw(ctx, sprite) {
    const pulse = Math.sin(this.phase) * 0.5 + 0.5;
    const size = this.baseSize * (0.5 + pulse * 0.5);
    const alpha = 0.4 + pulse * 0.6;
    const radius = size * 3; // matches the glow spread of the original gradient

    ctx.globalAlpha = alpha;
    ctx.drawImage(sprite, this.x - radius, this.y - radius, radius * 2, radius * 2);
    ctx.globalAlpha = 1;
  }
}

// Auto-initialize all firefly canvases — deferred so it never competes
// with the page's critical loading work (LCP / TBT window).
(function () {
  const instances = new Map();

  const initFireflies = () => {
    document.querySelectorAll('.firefly-canvas').forEach((canvas) => {
      if (!instances.has(canvas)) {
        instances.set(canvas, new FireflyParticles(canvas));
      }
    });
  };

  const scheduleInit = () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(initFireflies, { timeout: 2000 });
    } else {
      setTimeout(initFireflies, 500);
    }
  };

  if (document.readyState === 'complete') {
    scheduleInit();
  } else {
    window.addEventListener('load', scheduleInit);
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', initFireflies);
  document.addEventListener('shopify:section:unload', (event) => {
    const canvas = event.target.querySelector('.firefly-canvas');
    if (canvas && instances.has(canvas)) {
      instances.get(canvas).destroy();
      instances.delete(canvas);
    }
  });
})();